import React, { useEffect, useState } from "react";
import {
  Button,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { IBrand } from "../models/brand.ts";
import { IUser } from "../models/user.ts";
import { IStatus } from "../models/status.ts";
import { ITicketField } from "../models/ticket_fields.ts";
import { zendeskHttpRequest } from "../lib/api/zendeskHttpRequest.ts";
import { BrandSelect } from "./parts/BrandSelect.tsx";
import { UserSelect } from "./parts/UserSelect.tsx";
import { StatusSelect } from "./parts/StatusSelect.tsx";
import { TicketFieldSelect } from "./parts/TicketFieldSelect.tsx";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  ExportProgressModal,
  ExportProgressStatus,
} from "./parts/ExportProgressModal.tsx";
import { exportTicketDataAndAttachments } from "../usecases/exportTicketDataAndAttachments.ts";
import dayjs, { Dayjs } from "dayjs";
import { getPaginatedData } from "../lib/api/getPaginatedData.ts";
import { config, CsvEncoding } from "../lib/config.ts";
import { CsvEncodingSelect } from "./parts/CsvEncodingSelect.tsx";

export const TicketAppContainer: React.FC<{ token: string }> = ({ token }) => {
  const defaultBrandUrl = `https://${import.meta.env.VITE_ZENDESK_SUBDOMAIN}.zendesk.com`;

  const [visibleModal, setVisibleModal] = useState<boolean>(false);
  const [totalTicketsCount, setTotalTicketsCount] = useState<number>(0);
  const [downloadedTicketsCount, setDownloadedTicketsCount] =
    useState<number>(0);
  const [exportProgressInfo, setExportProgressInfo] = useState<{
    status: ExportProgressStatus;
    message: string;
  }>({ status: "Export", message: "" });

  const [brands, setBrands] = useState<IBrand[]>([]);
  const [selectBrand, setSelectBrand] = useState<IBrand>();

  const [users, setUsers] = useState<IUser[]>([]);
  const [selectUserIds, setSelectUserIds] = useState<number[]>();

  const [statuses, setStatuses] = useState<IStatus[]>([]);
  const [selectStatusIds, setSelectStatusIds] = useState<number[]>();

  const [targetProducts, setTargetProducts] = useState<ITicketField[]>([]);
  const [selectTargetProductValues, setSelectTargetProductValues] =
    useState<string[]>();

  const [inquiryTypes, setInquiryTypes] = useState<ITicketField[]>([]);
  const [selectInquiryValues, setSelectInquiryValues] = useState<string[]>();

  const [startDate, setStartDate] = useState<string>(
    dayjs().subtract(1, "month").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useState<string>(dayjs().format("YYYY-MM-DD"));

  const handleStartDateChange = (newValue: Dayjs | null) => {
    if (newValue) {
      setStartDate(newValue.format("YYYY-MM-DD"));
    }
  };

  const handleEndDateChange = (newValue: Dayjs | null) => {
    if (newValue) {
      setEndDate(newValue.format("YYYY-MM-DD"));
    }
  };

  const [startDateError, setStartDateError] = useState<boolean>(false);
  const [endDateError, setEndDateError] = useState<boolean>(false);

  const [ticketType, setTicketType] = useState<string>("all");

  const [commentKeyword, setCommentKeyword] = useState<string>("");

  const [csvEncoding, setCsvEncoding] = useState<CsvEncoding>(
    config.defaultCsvEncoding,
  );

  const handleUpdateProgressInfo = async (
    status: ExportProgressStatus,
    message: string,
  ) => {
    setExportProgressInfo({ status: status, message: message });
  };

  const handleUpdateTotalTicketsCount = async (count: number) => {
    setTotalTicketsCount(count);
  };

  const handleUpdateDownloadedTicketsCount = async (count: number) => {
    setDownloadedTicketsCount(count);
  };

  const TARGET_PRODUCT_ID = import.meta.env.VITE_TARGET_PRODUCT_ID;
  const INQUIRY_TYPE_ID = import.meta.env.VITE_INQUIRY_TYPE_ID;

  let searchQuery = "";
  if (selectBrand && selectBrand.id !== 0) {
    searchQuery += `brand:${selectBrand.id} `;
  }
  if (startDate) searchQuery += `created>=${startDate} `;
  if (endDate) searchQuery += `created<=${endDate} `;
  if (ticketType === "mail") searchQuery += "via:web via:mail ";
  if (ticketType === "call") searchQuery += "via:phone ";
  if (selectUserIds) {
    selectUserIds.forEach((id) => {
      searchQuery += `assignee:${id} `;
    });
  }
  if (selectStatusIds) {
    selectStatusIds.forEach((id) => {
      searchQuery += `custom_status_id:${id} `;
    });
  }
  if (config.useFixedTicketFields) {
    if (selectTargetProductValues) {
      selectTargetProductValues.forEach((value) => {
        searchQuery += `custom_field_${TARGET_PRODUCT_ID}:${value} `;
      });
    }
    if (selectInquiryValues) {
      selectInquiryValues.forEach((value) => {
        searchQuery += `custom_field_${INQUIRY_TYPE_ID}:${value} `;
      });
    }
  }
  if (commentKeyword) searchQuery += `comment:"${commentKeyword}" `;

  searchQuery = searchQuery.trim();
  useEffect(() => {
    const fetchBrands = async () => {
      const data = await zendeskHttpRequest<{ brands: IBrand[] }>(
        "brands",
        token,
        "GET",
      );
      const isAscii = (s: string) => /^[A-Za-z0-9]/.test(s);
      const sorted = [...data.brands].sort((a, b) => {
        if (a.default !== b.default) return a.default ? -1 : 1;
        if (isAscii(a.name) !== isAscii(b.name)) return isAscii(a.name) ? 1 : -1;
        return a.name.localeCompare(b.name, "ja");
      });
      const allBrand = { id: 0, brand_url: "all", name: "全て", default: false };
      sorted.unshift(allBrand);
      setBrands(sorted);
    };
    fetchBrands().catch(console.error);
  }, [token]);

  const handleSelectedBrandChange = (brand: IBrand) => {
    setSelectBrand(brand);
  };

  useEffect(() => {
    const fetchUsers = async () => {
      const data = await getPaginatedData<IUser>(
        "users?role[]=admin&role[]=agent",
        token,
        defaultBrandUrl,
        "users",
      );
      setUsers(data.filter((user) => user.role_type !== 1));
    };
    fetchUsers().catch(console.error);
  }, [token]);

  const handleSelectedUsersChange = (selectedUserIds: number[]) => {
    setSelectUserIds(selectedUserIds);
  };

  useEffect(() => {
    const fetchStatuses = async () => {
      const data = await zendeskHttpRequest<{
        custom_statuses: IStatus[];
      }>("custom_statuses", token, "GET");

      setStatuses(data.custom_statuses);
    };
    fetchStatuses().catch(console.error);
  }, [token]);

  const handleSelectedStatusIdsChange = (selectedStatusIds: number[]) => {
    setSelectStatusIds(selectedStatusIds);
  };

  useEffect(() => {
    if (!config.useFixedTicketFields) return;

    const fetchTargetProduct = async (
      fieldId: string,
      setState: React.Dispatch<React.SetStateAction<ITicketField[]>>,
    ) => {
      try {
        const data = await zendeskHttpRequest<{ ticket_field: ITicketField }>(
          `ticket_fields/${fieldId}`,
          token,
          "GET",
        );

        if (data.ticket_field) {
          setState([data.ticket_field]);
        } else {
          console.warn(`No data found for field ID: ${fieldId}`);
        }
      } catch (error) {
        console.error(`Error fetching ticket field (${fieldId}):`, error);
      }
    };

    fetchTargetProduct(TARGET_PRODUCT_ID, setTargetProducts);
    fetchTargetProduct(INQUIRY_TYPE_ID, setInquiryTypes);
  }, [token]);

  const handleSelectedTargetProductValuesChange = (
    selectedValues: string[],
  ) => {
    setSelectTargetProductValues(selectedValues);
  };

  const handleSelectedInquiryTypeValuesChange = (selectedValues: string[]) => {
    setSelectInquiryValues(selectedValues);
  };

  const isExportButtonDisabled = () => {
    return (
      !startDate || !endDate || !ticketType || startDateError || endDateError
    );
  };

  return (
    <Container maxWidth="md">
      <Grid container spacing={2} sx={{ mt: 5 }} alignItems="center">
        <Grid item xs={12}>
          <BrandSelect
            brands={brands}
            onSelectedBrandChange={handleSelectedBrandChange}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} alignItems="center" sx={{ mt: 3 }}>
        <Grid item xs={6}>
          <Typography variant="body2" textAlign="left">
            チケット作成日（期間指定/必須入力）
          </Typography>
        </Grid>{" "}
        <Grid item xs={6}>
          <Typography variant="body2" textAlign="left">
            チケット種別
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="開始日"
              format="YYYY/MM/DD"
              slotProps={{ calendarHeader: { format: "YYYY年MM月" } }}
              value={startDate ? dayjs(startDate) : null}
              onChange={(newValue) => handleStartDateChange(newValue)}
              onError={(error) => setStartDateError(!!error)}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={3}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="終了日"
              format="YYYY/MM/DD"
              slotProps={{ calendarHeader: { format: "YYYY年MM月" } }}
              value={endDate ? dayjs(endDate) : null}
              onChange={(newValue) => handleEndDateChange(newValue)}
              onError={(error) => setEndDateError(!!error)}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <RadioGroup
              row
              value={ticketType}
              onChange={(e) => setTicketType(e.target.value)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <FormControlLabel
                value="all"
                control={
                  <Radio
                    size="small"
                    sx={{
                      color: "#00897B",
                      "&.Mui-checked": { color: "#00897B" },
                    }}
                  />
                }
                label="全て"
              />
              <FormControlLabel
                value="mail"
                control={
                  <Radio
                    size="small"
                    sx={{
                      color: "#00897B",
                      "&.Mui-checked": { color: "#00897B" },
                    }}
                  />
                }
                label="メール"
              />
              <FormControlLabel
                value="call"
                control={
                  <Radio
                    size="small"
                    sx={{
                      color: "#00897B",
                      "&.Mui-checked": { color: "#00897B" },
                    }}
                  />
                }
                label="電話"
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container item xs={12} spacing={2} sx={{ mt: 3 }}>
        <Grid item xs={6}>
          <UserSelect
            users={users}
            onSelectedUsersChange={handleSelectedUsersChange}
          />
        </Grid>

        <Grid item xs={6}>
          <StatusSelect
            statuses={statuses}
            onSelectedStatusesChange={handleSelectedStatusIdsChange}
          />
        </Grid>
      </Grid>
      {config.useFixedTicketFields && (
        <Grid container spacing={2} sx={{ mt: 3 }}>
          <Grid item xs={6}>
            <TicketFieldSelect
              label="お問合せ製品"
              ticketFields={targetProducts}
              onSelectedTicketFieldValuesChange={
                handleSelectedTargetProductValuesChange
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TicketFieldSelect
              label="問合せ種別"
              ticketFields={inquiryTypes}
              onSelectedTicketFieldValuesChange={
                handleSelectedInquiryTypeValuesChange
              }
            />
          </Grid>
        </Grid>
      )}

      <Grid item xs={12} sx={{ mt: 3 }}>
        <TextField
          fullWidth
          label="コメントキーワード"
          variant="outlined"
          onChange={(e) => setCommentKeyword(e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <CsvEncodingSelect value={csvEncoding} onChange={setCsvEncoding} />
      </Grid>
      <Grid>
        <Button
          variant="contained"
          style={{ marginTop: "16px" }}
          disabled={isExportButtonDisabled()}
          onClick={async () => {
            setVisibleModal(true);
            await exportTicketDataAndAttachments(
              defaultBrandUrl, // ブランドURLはチケットエクスポートでは固定で良いため
              token,
              searchQuery,
              handleUpdateTotalTicketsCount,
              handleUpdateDownloadedTicketsCount,
              handleUpdateProgressInfo,
              csvEncoding,
            );
          }}
          sx={{
            backgroundColor: "#00897B",
            "&:hover": { backgroundColor: "#00695C" },
          }}
        >
          エクスポート実行
        </Button>
        <ExportProgressModal
          open={visibleModal}
          onClose={() => {
            setVisibleModal(false);
            setTotalTicketsCount(0);
            setDownloadedTicketsCount(0);
            setExportProgressInfo({ status: "Export", message: "" });
          }}
          totalCount={totalTicketsCount}
          downloadedCount={downloadedTicketsCount}
          progressInfo={exportProgressInfo}
        />
      </Grid>
    </Container>
  );
};
